'use client';

import { Bar, BarChart, CartesianGrid, Rectangle, XAxis, YAxis } from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';

// Default color palette for bars when no specific config color is provided
const DEFAULT_BAR_COLOR = '#0D9373';

export function AppBarChart({
  chartData = [],
  config = {},
  dataKey, // Required prop
  nameKey, // Required prop
  isLoading = false,
  className = '',
}: {
  chartData: any[];
  config?: ChartConfig;
  dataKey: string; // Required prop
  nameKey: string; // Required prop
  isLoading?: boolean;
  className?: string;
}) {
  // Ensure dataKey has a color in the config for the CSS variable fallback
  const enrichedConfig = {
    ...config,
    [dataKey]: config[dataKey] || { label: dataKey, color: DEFAULT_BAR_COLOR },
  };

  const data = chartData?.map(item => {
    const itemKey = item[nameKey] as string;
    return {
      ...item,
      fill: enrichedConfig[itemKey]?.color || enrichedConfig[dataKey]?.color || DEFAULT_BAR_COLOR,
    };
  });

  return (
    <div className="w-full overflow-hidden">
      {isLoading ? (
        <div className="p-4">
          <Skeleton className="h-64 w-full rounded-md bg-coal-200" />
        </div>
      ) : (
        <ChartContainer config={enrichedConfig} className={cn('aspect-auto h-[300px] w-full', className)}>
          <BarChart
            accessibilityLayer
            data={data}
            margin={{
              top: 10,
              left: 0,
              right: 10,
              bottom: 20,
            }}
          >
            <XAxis
              tickMargin={8}
              tickLine={false}
              axisLine={false}
              padding={{ left: 10, right: 10 }}
              dataKey={nameKey}
              tickFormatter={value => {
                const label = enrichedConfig[value]?.label || value;
                return label?.length > 14 ? label.slice(0, 14) + '…' : label;
              }}
              fontSize={11}
              interval={0}
            />
            <YAxis
              tickMargin={4}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="dot" nameKey={dataKey} hideLabel />}
            />
            <Bar
              dataKey={dataKey}
              strokeWidth={2}
              maxBarSize={60}
              radius={6}
              activeBar={({ ...props }) => {
                const itemKey = props.payload[nameKey] as string;
                const customColor = enrichedConfig[itemKey]?.color || props.fill;
                return (
                  <Rectangle
                    {...props}
                    fillOpacity={0.8}
                    stroke={customColor}
                    strokeDasharray={4}
                    strokeDashoffset={4}
                  />
                );
              }}
            />
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
}
